<template>
  <div class="min-h-screen bg-background">
    <Header />

    <main class="container py-8">
      <!-- Page Title -->
      <h1 class="text-3xl font-bold text-foreground md:text-4xl">
        {{ $t('search.title') }}
      </h1>
      <p class="mt-2 text-lg text-muted-foreground">{{ $t('search.subtitle') }}</p>

      <!-- Mode Toggle -->
      <div class="mt-6 flex gap-2">
        <NuxtLink
          :to="$localeRoute('/services')"
          class="rounded-xl border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
        >
          {{ $t('search.modes.services') }}
        </NuxtLink>
        <button
          :class="[
            'rounded-xl px-4 py-2 text-sm font-medium',
            searchMode === 'businesses'
              ? 'bg-primary text-primary-foreground'
              : 'border border-border text-muted-foreground hover:bg-muted',
          ]"
          @click="searchMode = 'businesses'"
        >
          {{ $t('search.modes.businesses') }}
        </button>
        <button
          :class="[
            'rounded-xl px-4 py-2 text-sm font-medium',
            searchMode === 'specialists'
              ? 'bg-primary text-primary-foreground'
              : 'border border-border text-muted-foreground hover:bg-muted',
          ]"
          @click="searchMode = 'specialists'"
        >
          {{ $t('search.modes.specialists') }}
        </button>
      </div>

      <!-- Filters -->
      <div class="mt-6 flex flex-wrap gap-3">
        <div class="relative min-w-48 flex-1">
          <Icon name="tabler:search" class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size="18" />
          <Input
            v-model="searchQuery"
            :placeholder="$ts('search.searchPlaceholder')"
            class="pl-10"
          />
        </div>
        <select
          v-model="selectedServiceType"
          class="rounded-xl border border-border bg-background px-4 py-2 text-sm"
        >
          <option value="">{{ $ts('search.filters.allServices') }}</option>
          <option v-for="st in serviceTypes" :key="st.value" :value="st.value">
            {{ $ts(`search.serviceTypes.${st.value}`) }}
          </option>
        </select>
        <SearchableSelect
          v-model="selectedCity"
          :options="citySelectOptions"
          :placeholder="$ts('search.filters.allCities')"
          :search-placeholder="$ts('search.filters.searchCities')"
          :no-results-text="$ts('search.filters.noCitiesFound')"
          class="min-w-44"
        />
      </div>

      <!-- Business Results -->
      <div v-if="searchMode === 'businesses'" class="mt-8">
        <Skeleton name="website-search-partners" :loading="partnersStatus === 'pending'">
          <template v-if="partnersData">
            <p class="mb-4 text-sm text-muted-foreground">
              {{ partnersList.length }} {{ $t('search.resultsFound', { count: partnersList.length }) }}
            </p>

            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <NuxtLink
                v-for="partner in partnersList"
                :key="partner._id"
                :to="$localeRoute(`/p/${partner.slug ?? partner._id}`)"
                class="rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <h3 class="text-lg font-semibold text-foreground">{{ partner.businessName }}</h3>
                <p
                  v-if="partner.description"
                  class="mt-1 line-clamp-2 text-sm text-muted-foreground"
                >
                  {{ partner.description }}
                </p>

                <div class="mt-3 flex items-center gap-2 text-sm">
                  <div v-if="partner.averageRating" class="flex items-center gap-1">
                    <Icon name="material-symbols:kid-star" class="text-amber-500" size="16" />
                    <span class="font-medium">{{ partner.averageRating }}</span>
                    <span class="text-muted-foreground">({{ partner.reviewCount }})</span>
                  </div>
                  <div v-if="partner.city" class="flex items-center gap-1 text-muted-foreground">
                    <Icon name="tabler:map-pin" size="14" />
                    {{ partner.city }}
                  </div>
                </div>

                <div class="mt-3 flex flex-wrap gap-1">
                  <span
                    v-for="st in partner.serviceTypes.slice(0, 3)"
                    :key="st"
                    class="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    {{ $ts(`search.serviceTypes.${st}`) }}
                  </span>
                </div>

                <div v-if="partner.services.length > 0" class="mt-3 border-t border-border pt-3">
                  <p class="text-xs text-muted-foreground">{{ $t('search.topServices') }}:</p>
                  <ul class="mt-1 space-y-1">
                    <li
                      v-for="svc in partner.services.slice(0, 2)"
                      :key="svc._id"
                      class="flex justify-between text-sm text-foreground"
                    >
                      <span>{{ svc.title }}</span>
                      <span v-if="svc.price" class="text-muted-foreground">
                        €{{ (svc.price / 100).toFixed(2) }}
                      </span>
                    </li>
                  </ul>
                </div>
              </NuxtLink>
            </div>

            <!-- Empty State -->
            <div v-if="partnersList.length === 0" class="mt-12 text-center">
              <Icon name="tabler:search-off" class="mx-auto text-muted-foreground" size="48" />
              <p class="mt-4 text-lg font-medium text-foreground">{{ $t('search.noResults.title') }}</p>
              <p class="text-muted-foreground">{{ $t('search.noResults.description') }}</p>
            </div>
          </template>

          <template #fallback>
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div v-for="i in 6" :key="i" class="h-56 animate-pulse rounded-2xl bg-muted" />
            </div>
          </template>
        </Skeleton>
      </div>

      <!-- Specialist Results -->
      <div v-if="searchMode === 'specialists'" class="mt-8">
        <Skeleton name="website-search-specialists" :loading="specialistsStatus === 'pending'">
          <template v-if="specialistsData">
            <p class="mb-4 text-sm text-muted-foreground">
              {{ specialistsList.length }} {{ $t('search.resultsFound', { count: specialistsList.length }) }}
            </p>

            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div
                v-for="specialist in specialistsList"
                :key="specialist._id"
                class="rounded-2xl border border-border bg-card p-5"
              >
                <div class="flex items-center gap-3">
                  <div class="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Icon name="tabler:user" class="text-primary" size="24" />
                  </div>
                  <div>
                    <h3 class="text-lg font-semibold text-foreground">{{ specialist.displayName }}</h3>
                    <span
                      v-if="specialist.isVerified"
                      class="flex items-center gap-0.5 text-xs text-primary"
                    >
                      <Icon name="tabler:circle-check-filled" size="14" />
                      {{ $t('search.verified') }}
                    </span>
                  </div>
                </div>

                <p
                  v-if="specialist.bio"
                  class="mt-2 line-clamp-2 text-sm text-muted-foreground"
                >
                  {{ specialist.bio }}
                </p>

                <div class="mt-3 flex items-center gap-3 text-sm">
                  <div v-if="specialist.averageRating" class="flex items-center gap-1">
                    <Icon name="material-symbols:kid-star" class="text-amber-500" size="16" />
                    <span class="font-medium">{{ specialist.averageRating }}</span>
                    <span class="text-muted-foreground">({{ specialist.reviewCount }})</span>
                  </div>
                  <span v-if="specialist.experienceYears" class="text-muted-foreground">
                    {{ specialist.experienceYears }}+ {{ $t('search.yearsExp') }}
                  </span>
                </div>

                <div class="mt-3 flex flex-wrap gap-1">
                  <span
                    v-for="st in specialist.serviceTypes"
                    :key="st"
                    class="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    {{ $ts(`search.serviceTypes.${st}`) }}
                  </span>
                </div>

                <div v-if="specialist.businesses.length > 0" class="mt-3 border-t border-border pt-3">
                  <p class="text-xs text-muted-foreground">{{ $t('search.worksAt') }}:</p>
                  <div class="mt-1 space-y-1">
                    <NuxtLink
                      v-for="biz in specialist.businesses"
                      :key="biz.partnerId"
                      :to="$localeRoute(`/p/${biz.slug ?? biz.partnerId}`)"
                      class="block text-sm text-primary hover:underline"
                    >
                      {{ biz.businessName }}
                      <span v-if="biz.city" class="text-muted-foreground">({{ biz.city }})</span>
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="specialistsList.length === 0" class="mt-12 text-center">
              <Icon name="tabler:search-off" class="mx-auto text-muted-foreground" size="48" />
              <p class="mt-4 text-lg font-medium text-foreground">{{ $t('search.noResults.title') }}</p>
              <p class="text-muted-foreground">{{ $t('search.noResults.description') }}</p>
            </div>
          </template>

          <template #fallback>
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div v-for="i in 6" :key="i" class="h-56 animate-pulse rounded-2xl bg-muted" />
            </div>
          </template>
        </Skeleton>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { definePageMeta, useI18n, useSeoMeta, useHead, useFetch, useSchemaOrg, defineWebPage } from '#imports';
import Header from '#components/base/Header.vue';
import Footer from '#components/base/Footer.vue';
import Input from '#bubba/shared/components/ui/input/Input.vue';
import SearchableSelect from '#bubba/shared/components/ui/searchable-select/SearchableSelect.vue';
import Skeleton from 'boneyard-js/vue';
import type { BasePartnerResult } from '#types/partner';
import type { ServiceItem } from '#types/services';

definePageMeta({
  layout: false,
});

useSchemaOrg([
  defineWebPage({ '@type': 'SearchResultsPage' }),
]);

const { $t, $ts, $localeRoute } = useI18n();

useHead({ title: () => $ts('search.seo.title') });
useSeoMeta({
  title: () => $ts('search.seo.title'),
  description: () => $ts('search.seo.description'),
});

type SearchMode = 'businesses' | 'specialists';
const searchMode = ref<SearchMode>('businesses');
const searchQuery = ref('');
const selectedServiceType = ref('');
const selectedCity = ref('');

interface PartnerResult extends BasePartnerResult {
  city?: string;
  services: ServiceItem[];
}

interface SpecialistResult {
  _id: string;
  name: string;
  displayName: string;
  title?: string;
  bio?: string;
  isVerified?: boolean;
  specialties: string[];
  serviceTypes: string[];
  experienceYears?: number;
  city?: string;
  averageRating?: number;
  reviewCount?: number;
  businesses: Array<{ _id: string; partnerId: string; businessName: string; city?: string; slug?: string }>;
}

const serviceTypes = [
  { value: 'grooming' },
  { value: 'walking' },
  { value: 'daycare' },
  { value: 'boarding' },
  { value: 'petsitting' },
  { value: 'training' },
  { value: 'transport' },
  { value: 'vet' },
];

// Fetch available cities
const { data: citiesData } = useFetch('/api/cities', {
  lazy: true,
  default: () => [] as Array<{ name: string; count: number }>,
});
const citiesList = computed(() => citiesData.value ?? []);

const citySelectOptions = computed(() => [
  { value: '', label: $ts('search.filters.allCities') },
  ...citiesList.value.map(city => ({
    value: city.name,
    label: `${city.name} (${city.count})`,
  })),
]);

// Business search query params
const businessQueryParams = computed(() => ({
  type: selectedServiceType.value || undefined,
  city: selectedCity.value || undefined,
  q: searchQuery.value || undefined,
}));

// Specialist search query params
const specialistQueryParams = computed(() => ({
  type: selectedServiceType.value || undefined,
  city: selectedCity.value || undefined,
  q: searchQuery.value || undefined,
}));

const { data: partnersData, status: partnersStatus } = useFetch<PartnerResult[]>('/api/partners', {
  query: businessQueryParams,
  lazy: true,
  default: () => [] as PartnerResult[],
});
const partnersList = computed(() => partnersData.value ?? []);

const { data: specialistsData, status: specialistsStatus } = useFetch<SpecialistResult[]>('/api/specialists', {
  query: specialistQueryParams,
  lazy: true,
  default: () => [] as SpecialistResult[],
});
const specialistsList = computed(() => specialistsData.value ?? []);
</script>
